/**
 * Daily Challenges Constants
 * XP values, levels, badges, and share platforms configuration
 */

import type { Badge, Level, SharePlatform, LevelTier } from '../types';

// ============================================================================
// XP Configuration
// ============================================================================

export const XP_VALUES = {
  SHARE_1: 100,
  SHARE_2: 150,
  SHARE_3: 300,
  SCAN_ITEM: 5,
  REFERRAL: 500,
  STREAK_BONUS: 50,
} as const;

export const MAX_SHARES_PER_DAY = 3;

export const SHARE_XP_REWARDS = [
  XP_VALUES.SHARE_1,
  XP_VALUES.SHARE_2,
  XP_VALUES.SHARE_3,
] as const;

// ============================================================================
// Level Configuration
// ============================================================================

export const LEVELS: Level[] = [
  { level: 1, title: 'First Steps', xpRequired: 0, tier: 'bronze', rewards: ['Welcome badge'] },
  { level: 2, title: 'Getting Started', xpRequired: 500, tier: 'bronze', rewards: ['+1 watchlist slot'] },
  { level: 3, title: 'Deal Seeker', xpRequired: 1500, tier: 'silver', rewards: ['Deal Seeker badge'] },
  { level: 4, title: 'Smart Shopper', xpRequired: 3000, tier: 'silver', rewards: ['+2 watchlist slots'] },
  { level: 5, title: 'Bargain Hunter', xpRequired: 5000, tier: 'gold', rewards: ['Bargain Hunter badge', '3 days Pro'] },
  { level: 6, title: 'Price Expert', xpRequired: 8000, tier: 'gold', rewards: ['+3 watchlist slots'] },
  { level: 7, title: 'As-Is Expert', xpRequired: 12000, tier: 'platinum', rewards: ['As-Is Expert badge', '7 days Pro'] },
  { level: 8, title: 'Deal Master', xpRequired: 18000, tier: 'platinum', rewards: ['+5 watchlist slots'] },
  { level: 9, title: 'IKEA Legend', xpRequired: 25000, tier: 'diamond', rewards: ['IKEA Legend badge', '14 days Pro'] },
  { level: 10, title: 'Grandmaster', xpRequired: 35000, tier: 'diamond', rewards: ['Grandmaster badge', '30 days Pro', 'Unlimited watchlist'] },
];

export const LEVEL_TIERS: Record<LevelTier, { color: string; bgColor: string; borderColor: string }> = {
  bronze: { color: 'text-amber-700', bgColor: 'bg-amber-100', borderColor: 'border-amber-300' },
  silver: { color: 'text-gray-600', bgColor: 'bg-gray-100', borderColor: 'border-gray-300' },
  gold: { color: 'text-yellow-600', bgColor: 'bg-yellow-100', borderColor: 'border-yellow-400' },
  platinum: { color: 'text-purple-600', bgColor: 'bg-purple-100', borderColor: 'border-purple-300' },
  diamond: { color: 'text-cyan-600', bgColor: 'bg-cyan-100', borderColor: 'border-cyan-400' },
};

// ============================================================================
// Badge Configuration
// ============================================================================

export const BADGES: Badge[] = [
  // Achievement badges
  { id: 'first_scan', name: 'First Scan', description: 'Scan your first As-Is item', icon: 'scan' },
  { id: 'store_explorer', name: 'Store Explorer', description: 'Scan at 3 different stores', icon: 'map-pin' },
  { id: 'referral_rookie', name: 'Referral Rookie', description: 'Get your first referral', icon: 'user-plus' },
  { id: 'referral_champion', name: 'Referral Champion', description: 'Get 10 referrals', icon: 'users' },
  { id: 'century_scanner', name: 'Century Scanner', description: 'Scan 100 As-Is items', icon: 'award' },
  { id: 'daily_dedicated', name: 'Daily Dedicated', description: 'Use app 7 days in a row', icon: 'calendar-check' },
  // Level badges
  { id: 'first_steps', name: 'First Steps', description: 'Reach Level 1', icon: 'footprints' },
  { id: 'deal_seeker', name: 'Deal Seeker', description: 'Reach Level 3', icon: 'search' },
  { id: 'bargain_hunter', name: 'Bargain Hunter', description: 'Reach Level 5', icon: 'target' },
  { id: 'as_is_expert', name: 'As-Is Expert', description: 'Reach Level 7', icon: 'star' },
  { id: 'ikea_legend', name: 'IKEA Legend', description: 'Reach Level 9', icon: 'trophy' },
  { id: 'grandmaster', name: 'Grandmaster', description: 'Reach Level 10', icon: 'crown' },
];

export const BADGE_ICONS: Record<string, string> = {
  'scan': 'Scan',
  'map-pin': 'MapPin',
  'user-plus': 'UserPlus',
  'users': 'Users',
  'award': 'Award',
  'calendar-check': 'CalendarCheck',
  'footprints': 'Footprints',
  'search': 'Search',
  'target': 'Target',
  'star': 'Star',
  'trophy': 'Trophy',
  'crown': 'Crown',
};

// ============================================================================
// Share Platform Configuration
// ============================================================================

export const SHARE_PLATFORMS: SharePlatform[] = [
  {
    id: 'twitter',
    name: 'X',
    icon: 'Twitter',
    color: 'text-white',
    bgColor: 'bg-black',
    hoverBgColor: 'hover:bg-gray-800',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'Facebook',
    color: 'text-white',
    bgColor: 'bg-[#1877F2]',
    hoverBgColor: 'hover:bg-[#166FE5]',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: 'Linkedin',
    color: 'text-white',
    bgColor: 'bg-[#0A66C2]',
    hoverBgColor: 'hover:bg-[#004182]',
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'MessageCircle',
    color: 'text-white',
    bgColor: 'bg-[#25D366]',
    hoverBgColor: 'hover:bg-[#128C7E]',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: 'Send',
    color: 'text-white',
    bgColor: 'bg-[#0088cc]',
    hoverBgColor: 'hover:bg-[#006699]',
  },
  {
    id: 'reddit',
    name: 'Reddit',
    icon: 'Globe',
    color: 'text-white',
    bgColor: 'bg-[#FF4500]',
    hoverBgColor: 'hover:bg-[#CC3700]',
  },
];

// ============================================================================
// Share Messages
// ============================================================================

export const SHARE_MESSAGE = {
  title: 'Rumio - Never Miss an IKEA Deal',
  text: "I've been using Rumio to track IKEA As-Is deals - saved so much on furniture! Check it out:",
  hashtags: ['IKEA', 'deals', 'furniture', 'savings', 'AsIs'],
} as const;

// ============================================================================
// Animation Configuration
// ============================================================================

export const CONFETTI_COLORS = [
  '#0077B6', // Primary blue
  '#ffda00', // Yellow
  '#ff6b6b', // Red
  '#4ecdc4', // Teal
  '#45b7d1', // Sky blue
  '#96ceb4', // Mint
] as const;

export const CONFETTI_COUNT = 12;

// ============================================================================
// Calendar Configuration
// ============================================================================

export const ACTIVITY_THRESHOLDS = {
  LOW: 50,    // 1-49 XP
  MEDIUM: 150, // 50-149 XP
  HIGH: 150,   // 150+ XP
} as const;

export const ACTIVITY_COLORS: Record<string, string> = {
  none: 'bg-gray-100',
  low: 'bg-green-200',
  medium: 'bg-green-400',
  high: 'bg-green-600',
};
